import type { ReferencedEntity } from "@cohesive/api-client";
import { getEntityData } from "@cohesive/ui-components";
import { Node } from "@xyflow/react";

/**
 * Converts the currently-selected ReactFlow canvas nodes into the
 * ReferencedEntity format expected by the messaging API.
 *
 * Shared across ChatPanel, QuickChatComposer and CanvasSessionPane —
 * do not duplicate this function in individual components.
 */
export function convertNodesToReferencedEntities(
  nodes: Node[],
): ReferencedEntity[] {
  return nodes
    .map((node) => {
      if (node.type === "entity" && node.data?.entity) {
        const entity = node.data.entity as any;
        const entityData = getEntityData(entity);
        return {
          entity_id: entityData?.id?.toString() || node.id,
          entity_type: entity.type || "unknown",
          name:
            entityData?.title ||
            entityData?.subject ||
            entityData?.name ||
            "Untitled",
          // Generic entities (the "typed for display, generic for placement"
          // shape most connectors drop on the canvas) fix the top-level
          // `platform` to the literal "generic" and carry the real source
          // platform in `data.platform_name` instead — read that first so the
          // agent can still tell a referenced Zoom/Gmail/etc. entity apart
          // from a plain generic one.
          platform: entityData?.platform_name || entity.platform || "cohesive",
        };
      }
      if (node.type === "section") {
        return {
          entity_id: node.id,
          entity_type: "canvas_section",
          name: (node.data?.title as string) || "Section",
          platform: "cohesive",
        };
      }
      if (node.type === "textNote") {
        return {
          entity_id: node.id,
          entity_type: "canvas_text_note",
          name: (node.data?.title as string) || "Note",
          platform: "cohesive",
        };
      }
      if (node.type === "canvasText") {
        const content = (node.data?.content as string) || "";
        return {
          entity_id: node.id,
          entity_type: "canvas_text_label",
          name: content.slice(0, 60) || "Text Label",
          platform: "cohesive",
        };
      }
      if (node.type === "canvasShape") {
        const content = (node.data?.content as string) || "";
        const shapeType = (node.data?.shape_type as string) || "shape";
        return {
          entity_id: node.id,
          entity_type: "canvas_shape",
          name: content || shapeType,
          platform: "cohesive",
        };
      }
      if (node.type === "canvasConnector") {
        return {
          entity_id: node.id,
          entity_type: "canvas_connector",
          name: "Connector",
          platform: "cohesive",
        };
      }
      return null;
    })
    .filter(Boolean) as ReferencedEntity[];
}
